import {
  Divider,
  EHeadingStyleVariant,
  EModalHeaderTextAlignVariant,
  EModalSize,
  ETextStyleVariant,
  GeneralTagIcon,
  Heading,
  Modal,
  Text,
} from '@outdoorsyco/bonfire';
import React, { Fragment, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useBreakpointContext } from '@/components/common/BreakpointContext';
import { calcDiscount } from '@/redux/selectors/listing/bill';
import { PriceType } from '@/redux/selectors/listing/page';

type TListingTilePriceDiscountProps = {
  weeklyDiscountPercentage?: number;
  monthlyDiscountPercentage?: number;
  originalPrice: number;
  currentPrice: number;
  period: PriceType;
};

export const ListingTilePriceDiscount = ({
  weeklyDiscountPercentage,
  monthlyDiscountPercentage,
  originalPrice,
  currentPrice,
  period,
}: TListingTilePriceDiscountProps) => {
  const { isMobile } = useBreakpointContext();
  const [showDiscounts, setShowDiscounts] = useState(false);

  const discount = calcDiscount(originalPrice, currentPrice);

  const rules = [
    weeklyDiscountPercentage && (
      <Text component="div" variant={ETextStyleVariant.MediumRegular}>
        <FormattedMessage
          id="VwfC9T"
          defaultMessage="Weekly (7+ {period, select, day {days} night {nights} other {}}): <b>{discount} off {period, select, day {daily} night {nightly} other {}} rate</b>"
          values={{
            period,
            discount: `${weeklyDiscountPercentage}%`,
            b: (value: React.ReactNode) => (
              <Text component="span" variant={ETextStyleVariant.MediumBold}>
                {value}
              </Text>
            ),
          }}
        />
      </Text>
    ),

    monthlyDiscountPercentage && (
      <Text component="div" variant={ETextStyleVariant.MediumRegular}>
        <FormattedMessage
          id="TTI7lp"
          defaultMessage="Monthly (28+ {period, select, day {days} night {nights} other {}}): <b>{discount} off {period, select, day {daily} night {nightly} other {}} rate</b>"
          values={{
            period,
            discount: `${monthlyDiscountPercentage}%`,
            b: (value: React.ReactNode) => (
              <Text component="span" variant={ETextStyleVariant.MediumBold}>
                {value}
              </Text>
            ),
          }}
        />
      </Text>
    ),
  ].filter(Boolean);

  if (!discount && !rules.length) return null;

  return (
    <>
      {
        // Prevent opening the tile when clicking on the tag
        <button
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();

            if (weeklyDiscountPercentage || monthlyDiscountPercentage) {
              setShowDiscounts(true);
            }
          }}>
          <Text
            component="div"
            variant={discount ? ETextStyleVariant.MediumBold : ETextStyleVariant.MediumRegular}
            className={`flex flex-nowrap whitespace-nowrap items-center px-1 mx-1 rounded gap-1 py-0.5 -my-px ${
              discount ? 'text-green-500 bg-green-30' : 'text-gray-50 bg-gray-10'
            }`}>
            <GeneralTagIcon className="w-3 h-3 shrink-0" />

            {discount ? (
              <FormattedMessage
                id="DTC4tH"
                defaultMessage="Save {discount}"
                values={{ discount: `${discount}%` }}
              />
            ) : (
              <FormattedMessage
                id="g6ZGNP"
                defaultMessage="{n, plural, one {# discount} other {# discounts}}"
                values={{ n: rules.length }}
              />
            )}
          </Text>
        </button>
      }

      {showDiscounts && (
        // Prevent clicks from bubbling up to the tile
        // eslint-disable-next-line
        <div onClick={e => e.stopPropagation()} className="hidden">
          <Modal
            isOpen={showDiscounts}
            onClose={() => setShowDiscounts(false)}
            size={isMobile ? EModalSize.Small : EModalSize.Medium}>
            <Modal.Actions />

            <Modal.Header textAlign={EModalHeaderTextAlignVariant.Center}>
              <Heading variant={isMobile ? EHeadingStyleVariant.H4 : EHeadingStyleVariant.H6}>
                <FormattedMessage defaultMessage="Discounts available" id="nvmXk3" />
              </Heading>
            </Modal.Header>

            <Modal.Content>
              <div className="p-2 md:py-6 md:px-4">
                {rules.map((rule, index) => (
                  <Fragment key={index}>
                    <div className="py-2 md:py-4">{rule}</div>
                    {rules[index + 1] && <Divider />}
                  </Fragment>
                ))}
              </div>
            </Modal.Content>
          </Modal>
        </div>
      )}
    </>
  );
};
